import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeedance30ProParams {
  /**
   * 内容数组，支持文本到视频、图像到视频（首帧）和图像到视频（首尾帧）三种模式
   */
  content: Array<{
    /**
     * 内容类型，可以是text或image_url
     */
    type: string;
    /**
     * 文本内容（仅当type为text时需要）
     */
    text?: string;
    /**
     * 图像信息（仅当type为image_url时需要）
     */
    image_url?: {
      /**
       * 图像URL或Base64数据URI
       */
      url: string;
    };
  }>;
  /**
   * 分辨率
   * @values 480p | 720p | 1080p
   */
  resolution?: '480p' | '720p' | '1080p';
  /**
   * 宽高比
   * @values 21:9 | 16:9 | 4:3 | 1:1 | 3:4 | 9:16 | 9:21 | keep_ratio | adaptive
   */
  ratio?: '21:9' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16' | '9:21' | 'keep_ratio' | 'adaptive';
  /**
   * 视频时长（秒），范围3-12秒
   * @range 3 - 12
   */
  duration?: number;
  /**
   * 帧率，可选16fps或24fps
   * @values 16 | 24
   */
  framespersecond?: 16 | 24;
  /**
   * 随机种子，范围-1到2^32-1，-1表示随机
   * @range -1 - ∞
   */
  seed?: number;
  /**
   * 是否添加水印，默认false
   * @default false
   */
  watermark?: boolean;
  /**
   * 是否固定摄像机
   */
  camerafixed?: boolean;
  /**
   * 是否返回最后一帧图像，默认false
   * @default false
   */
  return_last_frame?: boolean;
}

export interface VolcesSeedance30ProResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedance_3_0_pro
 *
 * 通过火山引擎SeeDance 3.0 Pro API创建视频生成任务。[API Documentation](https://www.volcengine.com/docs/82379/1520757)
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedance30Pro } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedance30Pro({
 *   // 参数见 VolcesSeedance30ProParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedance30ProParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedance30Pro(
  params: VolcesSeedance30ProParams,
): Promise<VolcesSeedance30ProResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedance_3_0_pro',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedance30ProResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
